/**
 * A coluna de fórmula — a LIGAÇÃO com o BlockSuite.
 *
 * Tudo o que este arquivo decide sozinho mora em `modelo.ts`, que roda sem navegador. O que
 * sobrou aqui é o que **exige o outro lado presente**: a fábrica `propertyType()` e a tabela
 * que traduz o nome do tipo lógico para a instância de `t`.
 *
 * ## O que fica gravado no documento, e o que NÃO fica
 *
 * `propertyData` guarda a **expressão** e o **formato de saída**. `rawValue` guarda **nada**:
 * a coluna é derivada, e o valor é calculado por linha.
 *
 * ⚠️ Essa segunda metade é decisão, não economia. A alternativa — materializar o resultado em
 * `cells` a cada recálculo — escreveria no CRDT toda vez que qualquer célula de origem
 * mudasse, que é exatamente o risco **R1** do dossiê (misturar escrita de servidor e de
 * cliente no mesmo campo) multiplicado pelo número de linhas. E entregaria valor velho para
 * quem abrisse o documento sem editá-lo.
 *
 * ## Por que o cálculo passa por `spacialProperties`, e não pelo render
 *
 * O contrato de `PropertyConfig` (`core/property/types.ts`) entrega a `rawValue.toJson` e a
 * `rawValue.toString` o par `{value, data, dataSource}` — e **nenhuma das duas recebe
 * `rowId`**. Uma fórmula sem saber de que linha se trata não é uma fórmula.
 *
 * Quem sabe é `spacialValueGet(rowId, propertyId, tipo)`, o mecanismo com que o próprio
 * Affine deriva `title`, `type`, `created-time` e `created-by`. Ele estava fechado nos quatro
 * tipos deles; o patch `0003` o abre pelo mesmo formato do `externalProperties`.
 *
 * **E a consequência é maior que exibir.** Com o valor vindo de `cellValueGet`, a soma no
 * rodapé (`statCalcType: 'sum'`), o filtro, a ordenação e o agrupamento passam a funcionar
 * sobre a coluna de fórmula — porque todos leem por ali. Calcular só no `cellRenderer`
 * entregaria uma coluna que **aparece e não soma**, e o doc da F4 pede as duas coisas
 * (`Valor líquido` somado por grupo, `Dias em atraso` filtrado e ordenado).
 */

import { propertyType, t } from '@blocksuite/affine/blocks/database';
import zod from 'zod';

import { FORMATOS, LOCALE_PADRAO, MOEDA_PADRAO } from './avaliador';
import {
  CASAS_MAXIMO, CASAS_MINIMO, celulaVazia, dadosPadraoDaFormula, ehErroDeFormula,
  textoDaCelula, tipoLogicoDoFormato, TIPO_DA_COLUNA_DE_FORMULA,
  type FormulaPropertyData,
} from './modelo';

export * from './modelo';

export const formulaColumnType = propertyType(TIPO_DA_COLUNA_DE_FORMULA);

/*
 * Os schemas `zod` são MONTADOS a partir do que o `modelo.ts` declara — a lista de formatos e
 * os limites de casas decimais têm um lugar só (Regra #9). Escrevê-los duas vezes faria o
 * schema aceitar um formato que a formatação não conhece, e o defeito apareceria na célula,
 * longe daqui.
 */
export const FormulaPropertyDataSchema = zod.object({
  expressao: zod.string().default(''),
  formato: zod.enum(FORMATOS as unknown as [string, ...string[]]).default('numero'),
  casas: zod.number().int().min(CASAS_MINIMO).max(CASAS_MAXIMO).optional(),
  locale: zod.string().default(LOCALE_PADRAO),
  moeda: zod.string().default(MOEDA_PADRAO),
});

export const FormulaRawValueSchema = zod
  .union([
    zod.number(),
    zod.string(),
    zod.boolean(),
    zod.null(),
    zod.object({
      __erroDeFormula: zod.literal(true),
      codigo: zod.string(),
      mensagem: zod.string(),
      rotulo: zod.string(),
    }),
  ])
  .nullable();

export const FormulaJsonValueSchema = zod
  .union([zod.number(), zod.string(), zod.boolean(), zod.null()])
  .nullable();

/** Nome do tipo lógico para a instância de `t`. A única tradução que exige o BlockSuite. */
const INSTANCIA_DE_TIPO = {
  number: () => t.number.instance(),
  date: () => t.date.instance(),
  boolean: () => t.boolean.instance(),
  string: () => t.string.instance(),
} as const;

export const formulaPropertyModelConfig = formulaColumnType.modelConfig({
  name: 'Fórmula',
  // Agrupar um kanban por coluna de fórmula é legítimo (agrupar por "está atrasado?"), mas
  // MUTÁVEL não: arrastar um card entre grupos tentaria escrever numa coluna derivada.
  kanbanGroup: { enabled: true, mutable: false },

  propertyData: {
    schema: FormulaPropertyDataSchema,
    default: dadosPadraoDaFormula,
  },

  rawValue: {
    schema: FormulaRawValueSchema,
    default: () => null,

    /**
     * ⚠️ **No-op deliberado** — igual ao `created-by` do upstream.
     *
     * A coluna é derivada. Sem isto, um duplo-clique na célula abriria edição e o valor
     * digitado iria para `cells`, ficando por cima do cálculo — a coluna passaria a mostrar
     * o que alguém digitou uma vez, e ninguém notaria.
     */
    setValue: () => {},

    toString: ({ value, data }) => textoDaCelula(value, data as FormulaPropertyData),

    // Colar texto numa coluna de fórmula não escreve nada. A alternativa seria interpretar o
    // texto colado como expressão nova, que é surpresa em vez de recurso.
    fromString: () => ({ value: null }),

    toJson: ({ value }) => (ehErroDeFormula(value) ? null : (value ?? null)),
  },

  jsonValue: {
    schema: FormulaJsonValueSchema,
    type: ({ data }) =>
      INSTANCIA_DE_TIPO[tipoLogicoDoFormato((data as FormulaPropertyData)?.formato)](),
    isEmpty: ({ value }) => celulaVazia(value),
  },
});
